import React from "react";
import { useApp } from "@/context/AppContext";

const Field = ({ label, hint, children, testid }) => (
  <label className="block" data-testid={`field-${testid}`}>
    <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-slate-500 mb-2">{label}</div>
    {children}
    {hint && <div className="text-[10px] font-sans text-slate-500 mt-1.5 italic leading-snug">{hint}</div>}
  </label>
);

const inputClass =
  "w-full bg-white border border-slate-300 px-3 py-2.5 text-sm font-mono tabular focus:border-slate-900 focus:ring-0";

const Section = ({ title, children }) => (
  <div className="border-t border-slate-200 pt-6 mt-6 first:border-t-0 first:pt-0 first:mt-0">
    <h3 className="font-serif text-xl text-slate-900 mb-4">{title}</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">{children}</div>
  </div>
);

const MethodToggle = ({ value, onChange, t }) => (
  <div className="grid grid-cols-2 border border-slate-300" data-testid="method-toggle">
    {[
      { v: "cnap", label: t.form.cnap },
      { v: "simple", label: t.form.simple },
    ].map((o, i) => (
      <button
        key={o.v}
        type="button"
        data-testid={`method-${o.v}`}
        onClick={() => onChange(o.v)}
        className={`px-4 py-3 text-xs font-mono uppercase tracking-[0.15em] ${i === 1 ? "border-l border-slate-300" : ""} ${
          value === o.v ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

const PensionForm = ({ data, setData, onCalculate, calculating }) => {
  const { t } = useApp();
  // Keep numeric inputs as raw strings so the user can clear them and type decimals freely.
  // Tax class stays a string. Empty values are coerced to 0 on calculate.
  const upd = (k) => (e) => setData({ ...data, [k]: e.target.value });

  return (
    <div className="bg-white border border-slate-200 p-6 md:p-8" data-testid="pension-form">
      <div className="mb-6">
        <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-slate-500 mb-2">{t.form.method}</div>
        <MethodToggle value={data.method} onChange={(v) => setData({ ...data, method: v })} t={t} />
      </div>

      <Section title={t.form.personal}>
        <Field label={t.form.currentAge} testid="current-age">
          <input type="number" min="18" max="80" value={data.current_age} onChange={upd("current_age")}
            className={inputClass} data-testid="input-current-age" />
        </Field>
        <Field label={t.form.retirementAge} testid="retirement-age">
          <input type="number" min="57" max="75" value={data.retirement_age} onChange={upd("retirement_age")}
            className={inputClass} data-testid="input-retirement-age" />
        </Field>
      </Section>

      <Section title={t.form.career}>
        <Field label={t.form.careerStartAge} hint={t.form.careerStartHint} testid="career-start">
          <input type="number" min="14" max="65" value={data.career_start_age} onChange={upd("career_start_age")}
            className={inputClass} data-testid="input-career-start" />
        </Field>
        <Field label={t.form.educationYears} hint={t.form.educationYearsHint} testid="education-years">
          <input type="number" min="0" max="10" value={data.education_years} onChange={upd("education_years")}
            className={inputClass} data-testid="input-education-years" />
        </Field>
        <Field label={t.form.pastGrowth} hint={t.form.pastGrowthHint} testid="past-growth">
          <input type="text" inputMode="decimal" pattern="[0-9]*[.,]?[0-9]*" value={data.past_salary_growth} onChange={upd("past_salary_growth")}
            className={inputClass} data-testid="input-past-growth" />
        </Field>
      </Section>

      <Section title={t.form.salary}>
        <Field label={t.form.monthlySalary} hint={t.form.salaryHint} testid="monthly-salary">
          <input type="number" min="0" step="50" value={data.monthly_gross_salary} onChange={upd("monthly_gross_salary")}
            className={inputClass} data-testid="input-monthly-salary" />
        </Field>
        <Field label={t.form.futureGrowth} hint={t.form.futureGrowthHint} testid="future-growth">
          <input type="text" inputMode="decimal" pattern="[0-9]*[.,]?[0-9]*" value={data.future_salary_growth} onChange={upd("future_salary_growth")}
            className={inputClass} data-testid="input-future-growth" />
        </Field>
      </Section>

      <Section title={t.form.family}>
        <Field label={t.form.taxClass} testid="tax-class">
          <select value={data.tax_class} onChange={upd("tax_class")} className={inputClass} data-testid="input-tax-class">
            <option value="1">{t.form.class1}</option>
            <option value="1a">{t.form.class1a}</option>
            <option value="2">{t.form.class2}</option>
          </select>
        </Field>
        <Field label={t.form.children} testid="children">
          <input type="number" min="0" max="10" value={data.children} onChange={upd("children")}
            className={inputClass} data-testid="input-children" />
        </Field>
      </Section>

      <div className="mt-8">
        <button
          type="button"
          onClick={onCalculate}
          disabled={calculating}
          data-testid="btn-calculate"
          className="w-full md:w-auto px-8 py-3.5 bg-slate-900 text-white text-xs font-mono uppercase tracking-[0.2em] hover:bg-slate-800 disabled:opacity-50"
        >
          {calculating ? "..." : t.form.calculate}
        </button>
      </div>
    </div>
  );
};

export default PensionForm;
